import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Columns,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { itemService } from '@/services/itemService'
import { batchItemService } from '@/services/batchItemService'
import { isGoodsMaterialType, type BatchItemVm, type ItemEntity, type ItemLookupOption } from '@/api/types'

const PAGE_SIZE = 10

type ColumnKey =
  | 'code'
  | 'reference'
  | 'name'
  | 'category'
  | 'subCategory'
  | 'availableQty'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'itemBatches.columns.code' },
  { key: 'reference', labelKey: 'itemBatches.columns.reference' },
  { key: 'name', labelKey: 'itemBatches.columns.name' },
  { key: 'category', labelKey: 'itemBatches.columns.category' },
  { key: 'subCategory', labelKey: 'itemBatches.columns.subCategory' },
  { key: 'availableQty', labelKey: 'itemBatches.columns.availableQty' },
]

export const ItemBatchList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const [rows, setRows] = useState<BatchItemVm[]>([])
  const [itemEntities, setItemEntities] = useState<ItemEntity[]>([])
  const [materialTypes, setMaterialTypes] = useState<ItemLookupOption[]>([])
  const [categories, setCategories] = useState<ItemLookupOption[]>([])
  const [subCategories, setSubCategories] = useState<ItemLookupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCountFromApi, setTotalCountFromApi] = useState(0)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    reference: true,
    name: true,
    category: true,
    subCategory: true,
    availableQty: true,
  })

  useEffect(() => {
    if (!branchId) return
    const loadItems = async () => {
      try {
        const [itemsRes, cats, subs, types] = await Promise.all([
          itemService.getAllItems(branchId, 1, 2000),
          itemService.getCategories(branchId),
          itemService.getSubCategories(branchId),
          itemService.getMaterialTypesLookups(),
        ])
        setItemEntities(itemsRes.items)
        setCategories(cats)
        setSubCategories(subs)
        setMaterialTypes(types || [])
      } catch {
        setItemEntities([])
        setCategories([])
        setSubCategories([])
        setMaterialTypes([])
      }
    }
    void loadItems()
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await batchItemService.getAll(branchId, pageNumber, PAGE_SIZE)
        setRows(res.items)
        setTotalCountFromApi(res.totalCount)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        setTotalCountFromApi(0)
        if (!isNoData) toast.error(error.response?.data?.message || t('itemBatches.failedToLoad'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, t])

  const itemById = useMemo(() => {
    const map: Record<number, ItemEntity> = {}
    itemEntities.forEach((it) => {
      map[it.itemId] = it
    })
    return map
  }, [itemEntities])

  const itemNameById = useMemo(() => {
    const map: Record<number, string> = {}
    itemEntities.forEach((it) => {
      const name =
        language === 'ar'
          ? it.itemNameAr || it.itemNameEn || it.itemCode
          : it.itemNameEn || it.itemNameAr || it.itemCode
      map[it.itemId] = name
    })
    return map
  }, [itemEntities, language])

  const categoryNameById = useMemo(() => {
    const map: Record<number, string> = {}
    categories.forEach((c) => {
      map[c.id] = language === 'ar' ? c.nameAr || c.nameEn : c.nameEn || c.nameAr
    })
    return map
  }, [categories, language])

  const subCategoryNameById = useMemo(() => {
    const map: Record<number, string> = {}
    subCategories.forEach((c) => {
      map[c.id] = language === 'ar' ? c.nameAr || c.nameEn : c.nameEn || c.nameAr
    })
    return map
  }, [subCategories, language])

  const goodsItemIds = useMemo(() => {
    const typeIds = new Set(materialTypes.filter(isGoodsMaterialType).map((x) => x.id))
    if (typeIds.size === 0) return null
    return new Set(itemEntities.filter((it) => typeIds.has(Number(it.type))).map((it) => it.itemId))
  }, [materialTypes, itemEntities])

  const filtered = useMemo(() => {
    const goodsRows = goodsItemIds ? rows.filter((r) => goodsItemIds.has(r.itemId)) : rows
    const q = searchQuery.trim().toLowerCase()
    if (!q) return goodsRows
    return goodsRows.filter((r) => {
      const item = itemById[r.itemId]
      const cat =
        item?.itemCategoryId != null ? categoryNameById[item.itemCategoryId] || '' : ''
      const sub =
        item?.subCategoryId != null ? subCategoryNameById[item.subCategoryId] || '' : ''
      return [
        r.code,
        r.reference,
        itemNameById[r.itemId] || String(r.itemId || ''),
        cat,
        sub,
        String(r.availableQuantity),
      ].some((field) => String(field).toLowerCase().includes(q))
    })
  }, [rows, searchQuery, itemNameById, itemById, categoryNameById, subCategoryNameById, goodsItemIds])

  const totalCount = goodsItemIds != null || searchQuery.trim() ? filtered.length : totalCountFromApi
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)
  const pageSlice = searchQuery.trim()
    ? filtered.slice(0, PAGE_SIZE)
    : filtered

  const handleAdd = () => navigate('/inventory/item-batches/new')

  const handleEdit = (row: BatchItemVm) => {
    navigate(`/inventory/item-batches/${row.id}/edit`)
  }

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('itemBatches.nothingToCopy'))
      return
    }
    const header = [
      'Code',
      'Reference',
      'Name',
      'Category',
      'Sub Category',
      'Available Qty',
    ]
    const lines = filtered.map((r) => {
      const item = itemById[r.itemId]
      const cat =
        item?.itemCategoryId != null ? categoryNameById[item.itemCategoryId] || '' : ''
      const sub =
        item?.subCategoryId != null ? subCategoryNameById[item.subCategoryId] || '' : ''
      return [
        r.code,
        r.reference,
        itemNameById[r.itemId] || '',
        cat,
        sub,
        r.availableQuantity,
      ].join('\t')
    })
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('itemBatches.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('itemBatches.nothingToExport'))
      return
    }
    const header =
      'Code,Reference,Name,Category,Sub Category,Available Qty'
    const body = pageSlice
      .map((r) => {
        const item = itemById[r.itemId]
        const cat =
          item?.itemCategoryId != null ? categoryNameById[item.itemCategoryId] || '' : ''
        const sub =
          item?.subCategoryId != null ? subCategoryNameById[item.subCategoryId] || '' : ''
        return [r.code, r.reference, itemNameById[r.itemId] || '', cat, sub, r.availableQuantity]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      })
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `item_batches_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('itemBatches.exported'))
  }

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('itemBatches.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('itemBatches.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('itemBatches.addItem')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('itemBatches.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('itemBatches.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('itemBatches.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('itemBatches.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('itemBatches.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 min-w-[72px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="h-12 min-w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.reference')}
                    </TableHead>
                  )}
                  {visibleColumns.name && (
                    <TableHead className="h-12 min-w-[120px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.name')}
                    </TableHead>
                  )}
                  {visibleColumns.category && (
                    <TableHead className="h-12 min-w-[96px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.category')}
                    </TableHead>
                  )}
                  {visibleColumns.subCategory && (
                    <TableHead className="h-12 min-w-[96px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.subCategory')}
                    </TableHead>
                  )}
                  {visibleColumns.availableQty && (
                    <TableHead className="h-12 min-w-[120px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('itemBatches.columns.availableQty')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('common.edit')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleCount + 1}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {loading ? (
                        <div className="p-12 text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                        </div>
                      ) : (
                        t('itemBatches.empty')
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((row) => {
                    const linkedItem = itemById[row.itemId]
                    const categoryLabel =
                      linkedItem?.itemCategoryId
                        ? categoryNameById[linkedItem.itemCategoryId] || '-'
                        : '-'
                    const subCategoryLabel =
                      linkedItem?.subCategoryId
                        ? subCategoryNameById[linkedItem.subCategoryId] || '-'
                        : '-'
                    return (
                    <TableRow
                      key={row.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {visibleColumns.code && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>
                      )}
                      {visibleColumns.reference && (
                        <TableCell className="px-4 py-2 text-center text-sm text-muted-foreground">
                          {row.reference}
                        </TableCell>
                      )}
                      {visibleColumns.name && (
                        <TableCell className="px-4 py-2 text-center text-sm font-medium">
                          {itemNameById[row.itemId] || row.itemId || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.category && (
                        <TableCell className="px-4 py-2 text-center text-sm">{categoryLabel}</TableCell>
                      )}
                      {visibleColumns.subCategory && (
                        <TableCell className="px-4 py-2 text-center text-sm">{subCategoryLabel}</TableCell>
                      )}
                      {visibleColumns.availableQty && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.availableQuantity}</TableCell>
                      )}
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEdit(row)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('itemBatches.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
